/**
 * ShellHeaderV2 — redesigned (Lovable) sticky app header shared by the admin
 * app, the Operations view and department workspaces.
 *
 *   row 1: logo tile · SATELLITE OS · | · <label in accent> ······ actions
 *   row 2: tab strip (scrolls horizontally on small screens) · MORE ▾
 *
 * Purely presentational: the caller owns tab state and passes its existing
 * action controls (bell, theme, settings…) as `actions`.
 */
import { useEffect, useRef, useState } from 'react';
import '../../design/v2.css';

const clean = l => String(l).replace(/^[^A-Za-z0-9]+/, '').replace(/\s+/g, ' ').trim();

export function LogoTile({ size = 36 }) {
  return (
    <span className="smc2-logo-tile" style={{ width: size, height: size }}>
      <img src="/logo-light.png" alt="Spacemancraft" />
    </span>
  );
}

// Numbered "new updates" badge on a tab
export function CountBadge({ n }) {
  if (!(n > 0)) return null;
  return <span className="smc2-count" aria-label={`${n} new`}>{n > 99 ? '99+' : n}</span>;
}

export default function ShellHeaderV2({
  label, accent = 'var(--smc-ac)', tabs = [], moreTabs = [], activeTab, onTab,
  onLogo, actions, chip, dots = {},
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const activeTabRef = useRef(null);
  const headerRef = useRef(null);
  const activeInMore = moreTabs.find(([v]) => v === activeTab);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = e => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const esc = e => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      window.removeEventListener('keydown', esc);
    };
  }, [moreOpen]);

  // Publish the header's real height as --smc-nav-h so sticky sub-bars
  // (team tabs, attendance tabs, banners) sit right under it at any width.
  useEffect(() => {
    const el = headerRef.current;
    const root = document.documentElement;
    if (!el) return undefined;
    const apply = () => root.style.setProperty('--smc-nav-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      root.style.removeProperty('--smc-nav-h');
    };
  }, []);

  // Keep the active tab visible in the scrolling strip (phones)
  useEffect(() => {
    activeTabRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  const pick = v => { setMoreOpen(false); onTab?.(v); };

  return (
    <header ref={headerRef} className="smc2-header" style={{ '--dept': accent }}>
      <div className="smc2-header-row">
        <button type="button" className="smc2-brand" onClick={onLogo} aria-label="Home">
          <LogoTile />
          <span className="smc2-hud smc2-hide-sm" style={{ color: 'var(--smc-tx3)' }}>SATELLITE OS</span>
        </button>
        <span className="smc2-divider smc2-hide-xs" />
        <span className="smc2-hud smc2-dept-label">{label}</span>
        {chip}
        <div className="smc2-actions">{actions}</div>
      </div>

      {(tabs.length > 0 || moreTabs.length > 0) && (
        <div className="smc2-tabrow">
          <nav className="smc2-tabs" aria-label="Sections">
            {tabs.map(([v, l]) => (
              <button key={v} type="button" ref={v === activeTab ? activeTabRef : undefined}
                className={`smc2-tab${v === activeTab ? ' is-active' : ''}`}
                aria-current={v === activeTab ? 'page' : undefined}
                onClick={() => pick(v)}>
                {clean(l)}
                <CountBadge n={dots[v]} />
              </button>
            ))}
          </nav>
          {moreTabs.length > 0 && (
            <div className="smc2-more" ref={moreRef}>
              <button type="button" aria-haspopup="menu" aria-expanded={moreOpen}
                className={`smc2-tab smc2-more-btn${activeInMore ? ' is-active' : ''}`}
                onClick={() => setMoreOpen(o => !o)}>
                {activeInMore ? `MORE · ${clean(activeInMore[1])}` : 'MORE'}
                <CountBadge n={moreTabs.reduce((sum, [v]) => sum + (dots[v] || 0), 0)} />
                <span aria-hidden>▾</span>
              </button>
              {moreOpen && (
                <div className="smc2-menu" role="menu">
                  {moreTabs.map(([v, l]) => (
                    <button key={v} type="button" role="menuitem"
                      className={`smc2-menu-item${v === activeTab ? ' is-active' : ''}`}
                      onClick={() => pick(v)}>
                      {clean(l)}
                      <CountBadge n={dots[v]} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
